import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'core-js/stable/symbol';
import 'core-js/stable/set';
import 'core-js/stable/map';
import 'core-js/stable/object';
import 'core-js/stable/promise';
import 'core-js/stable/string';
import 'core-js/stable/regexp';
import smoothscroll from 'smoothscroll-polyfill';

import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import LogRocket from 'logrocket';
import { Provider } from 'react-redux'
import { Route, Switch } from 'react-router-dom';
import { Grid, Loader } from 'semantic-ui-react';
import { QueryCache, ReactQueryCacheProvider } from "react-query";
import { createStore, applyMiddleware, compose } from 'redux'
import { createBrowserHistory } from 'history'
import { ConnectedRouter, routerMiddleware } from 'connected-react-router'
import thunkMiddleware from 'redux-thunk'
import { apiMiddleware } from 'redux-api-middleware';
import * as Sentry from '@sentry/react';
import rootReducer from './reducers'
import gaMiddleware from './middlewares/ga'
import authMiddleware from './middlewares/auth'
import AppSettings from './config';
import CmsWrapper from './CmsWrapper';
import EmployeeWrapper from './components/EmployeeWrapper';
import LogRocketProvider from './components/LogRocketProvider'
import './index.css';
import * as serviceWorker from './serviceWorker';

const App = lazy(() => import('./App'));
const Landing = lazy(() => import('./Landing'));
const Login = lazy(() => import('./Login'));
const Logout = lazy(() => import('./Logout'));
const ForgotAccount = lazy(() => import('./ForgotAccount'));
const ResetPassword = lazy(() => import( './ResetPassword'));
const Verify = lazy(() => import('./Verify'));
const SiteInfoApp = lazy(() => import('./SiteInfoApp'));
const CatalogApp = lazy(() => import('./CatalogApp'));
const PublicApp = lazy(() => import('./PublicApp'));
const CrmApp = lazy(() => import('./CrmApp'));
const CodeApp = lazy(() => import('./CodeApp'));
const CommunicationsApp = lazy(() => import('./CommunicationsApp'));
const PermitsApp = lazy(() => import('./PermitsApp'));
const GoAutomationApp = lazy(() => import('./GoAutomationApp'));
const WhatsNewApp = lazy(() => import('./WhatsNewApp'))
const AdminApp = lazy(() => import('./AdminApp'));

const SentryConfig = {
  dsn: AppSettings.SENTRY_DSN,
  release: process.env.SENTRY_RELEASE
}
Sentry.init(SentryConfig);

smoothscroll.polyfill();
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  configureScopeWithState: (scope, state:any) => {
    scope.setTag('gogovapp.site', state.site.site);
  }
});
const history = createBrowserHistory()
const store = createStore(rootReducer(history), undefined, compose(
  applyMiddleware(thunkMiddleware, routerMiddleware(history), apiMiddleware, authMiddleware, gaMiddleware, LogRocket.reduxMiddleware()),
  sentryReduxEnhancer
))
const queryCache = new QueryCache({
  defaultConfig: {
    queries: {
      staleTime: Infinity,
    },
  },
})

const loader                                    = (
  <Grid verticalAlign='middle' centered>
    <div style={{ margin: 'auto', height: '100%'}}>
      <Loader active={true}>
        Loading...
      </Loader>
    </div>
  </Grid>
)

const routing = (
  <ReactQueryCacheProvider queryCache={queryCache}>
    <Provider store={store as any}>
      <LogRocketProvider>
        <ConnectedRouter history={history}>
          <Suspense fallback={loader}>
            <Switch>
              <Route path="/" exact component={App} />
              <Route path="/dashboard" component={App} />
              <Route path="/site-info" component={SiteInfoApp} />
              <Route path="/catalog" component={CatalogApp} />
              <Route path="/:site/landing" component={Landing} />
              <Route path="/:site/public" component={PublicApp}/>
              <Route path="/:site/whats-new">
                <EmployeeWrapper render={() => <WhatsNewApp/>}/>
              </Route>
              <Route path="/:site/permits">
                <EmployeeWrapper render={() => <PermitsApp/>}/>
              </Route>
              <Route path="/:site/communications">
                <EmployeeWrapper render={() => <CommunicationsApp/>}/>
              </Route>
              <Route path="/:site/automation">
                <EmployeeWrapper render={() => <GoAutomationApp/>}/>
              </Route>
              <Route path="/:site/code">
                <EmployeeWrapper render={() => <CodeApp/>}/>
              </Route>
              <Route path="/:site/crm">
                <EmployeeWrapper render={() => <CrmApp/>}/>
              </Route>
              <Route path="/:site/admin">
                <EmployeeWrapper render={() => <AdminApp/>}/>
              </Route>
              <Route path="/:site/forgot" component={ForgotAccount}/>
              <Route path="/:site/reset" component={ResetPassword}/>
              <Route path="/:site/welcome" component={Verify}/>
              <Route path="/:site/logout" component={Logout}/>
              <Route path="/:site/login" component={Login}/>
              <Route path="/:site" exact component={Login}/>
            </Switch>
          </Suspense>
        </ConnectedRouter>
      </LogRocketProvider>
    </Provider>
  </ReactQueryCacheProvider>
)

ReactDOM.render(routing, document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
